import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, EnvelopeSimple, Phone, User as UserIcon, Briefcase, FloppyDisk } from "@phosphor-icons/react";
import { formatDate } from "@/lib/utils";
import { SALES_STATUS_COLORS } from "@/pages/Sales";

const LEAD_STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  qualified: "bg-indigo-50 text-indigo-700",
  won: "bg-emerald-50 text-emerald-700",
  lost: "bg-rose-50 text-rose-700",
};

export default function SalesClientDetail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const email = params.get("email") || "";

  const [client, setClient] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const todayStr = new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/sales/clients/detail", { params: { email } }),
        api.get("/sales/assignees"),
      ]);
      setClient(c.data);
      setAssignees(a.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load client");
      navigate("/sales");
    } finally {
      setLoading(false);
    }
  }, [email, navigate]);

  useEffect(() => { if (email) load(); else navigate("/sales"); }, [email, load, navigate]);

  const patch = async (body, okMsg) => {
    try {
      await api.patch("/sales/clients/detail", body, { params: { email } });
      if (okMsg) toast.success(okMsg);
      const { data } = await api.get("/sales/clients/detail", { params: { email } });
      setClient(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const { data } = await api.post("/sales/clients/notes", { text, date: todayStr }, { params: { email } });
      setClient((prev) => ({ ...prev, note_entries: data.note_entries }));
      setNewNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  if (loading || !client) {
    return <div className="text-sm text-slate-500" data-testid="sales-detail-loading">Loading…</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
      data-testid="sales-client-detail"
    >
      <div className="flex items-center justify-between">
        <Link to="/sales" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back to sales
        </Link>
        <span
          className={`px-3 py-1 rounded-sm text-xs font-medium ${SALES_STATUS_COLORS[client.status] || "bg-slate-100 text-slate-700"}`}
          data-testid="sales-detail-status-badge"
        >
          {client.status}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h1 className="font-display text-2xl text-slate-900" data-testid="sales-detail-name">{client.name}</h1>
            <div className="mt-3 space-y-1.5 text-sm text-slate-600">
              <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-slate-400" /> <span className="font-mono">{client.email}</span></div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> {client.phone || <span className="text-slate-400">No number on file</span>}</div>
            </div>
          </div>

          {/* Leads from this client */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-6 py-4 border-b border-slate-200 label-tracking">
              Leads from this client ({client.lead_count})
            </div>
            {client.leads.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No leads found.</p>
            ) : (
              <ul data-testid="sales-detail-leads">
                {client.leads.map((l) => (
                  <li key={l.id} className="px-6 py-4 border-b border-slate-100 last:border-b-0 flex items-start gap-3">
                    <Briefcase size={16} className="text-slate-300 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      {isAdmin ? (
                        <Link to={`/leads/${l.id}`} className="text-sm font-medium text-slate-900 hover:underline truncate block">
                          {l.email_subject || "(no subject)"}
                        </Link>
                      ) : (
                        <span className="text-sm font-medium text-slate-900 truncate block">
                          {l.email_subject || "(no subject)"}
                        </span>
                      )}
                      {l.summary && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{l.summary}</p>}
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[0.7rem] text-slate-400">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm font-medium ${LEAD_STATUS_COLORS[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</span>
                        {l.source && <span>· {l.source}</span>}
                        {l.received_at && <span>· {formatDate(l.received_at)}</span>}
                        {(l.assigned_to_name || []).length > 0 && <span>· {l.assigned_to_name.join(", ")}</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sidebar — sales controls */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="sales-detail-controls">
            <div className="label-tracking">Sales</div>

            <div>
              <Label className="text-[11px] text-slate-500">Status</Label>
              <Select value={client.status} onValueChange={(v) => patch({ status: v }, `Moved to ${v}`)}>
                <SelectTrigger data-testid="sales-status-select" className="mt-1 rounded-sm h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Lead", "In Progress", "Finalized"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] text-slate-500">Assigned user</Label>
              <Select
                value={client.assigned_to || "__none__"}
                onValueChange={(v) => patch({ assigned_to: v === "__none__" ? "" : v }, "Assignment updated")}
              >
                <SelectTrigger data-testid="sales-assign-select" className="mt-1 rounded-sm h-9 text-sm">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {assignees.map((a) => (
                    <SelectItem key={a.email} value={a.email}>
                      {a.name}{a.department ? ` · ${a.department}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          </div>

          {/* Notes — append-only, auto-dated journal (entries cannot be edited) */}
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sales-notes-panel">
            <div className="label-tracking mb-3">Notes</div>

            {/* Add a new note — the date is stamped automatically */}
            <div className="rounded-sm border border-slate-200 bg-slate-50/60 p-3">
              <div className="text-[11px] font-mono text-slate-500 mb-1" data-testid="sales-note-date">{todayStr}</div>
              <Textarea
                data-testid="sales-note-input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                placeholder="Type a note…"
                className="rounded-sm text-sm bg-white"
              />
              <div className="flex justify-end mt-2">
                <Button
                  data-testid="sales-note-save"
                  size="sm"
                  disabled={!newNote.trim() || savingNote}
                  onClick={saveNote}
                  className="h-8 rounded-sm bg-slate-900 hover:bg-slate-800 text-xs gap-1"
                >
                  <FloppyDisk size={12} /> Save note
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Saved notes are permanent and can't be edited.</p>
            </div>

            {/* Past notes — newest first, read-only */}
            <ol className="mt-4 space-y-3" data-testid="sales-notes-list">
              {(client.note_entries || []).length === 0 && !client.notes ? (
                <p className="text-xs text-slate-400">No notes yet.</p>
              ) : (
                <>
                  {(client.note_entries || []).map((n, i) => (
                    <li key={i} className="relative pl-4 text-xs" data-testid={`sales-note-${i}`}>
                      <span className="absolute left-0 top-1 h-1.5 w-1.5 rounded-full bg-slate-300" />
                      <div className="font-mono text-[11px] text-slate-500">{n.date} · {n.by_name}</div>
                      <p className="mt-0.5 text-slate-700 whitespace-pre-wrap">{n.text}</p>
                    </li>
                  ))}
                  {client.notes && (
                    <li className="relative pl-4 text-xs" data-testid="sales-note-legacy">
                      <span className="absolute left-0 top-1 h-1.5 w-1.5 rounded-full bg-slate-200" />
                      <div className="font-mono text-[11px] text-slate-400">earlier note</div>
                      <p className="mt-0.5 text-slate-600 whitespace-pre-wrap">{client.notes}</p>
                    </li>
                  )}
                </>
              )}
            </ol>
          </div>

          {/* Activity log */}
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sales-activity">
            <div className="label-tracking mb-3">Activity log</div>
            {(client.activity || []).length === 0 ? (
              <p className="text-xs text-slate-400">No activity yet. Status changes and assignments will appear here.</p>
            ) : (
              <ol className="space-y-3">
                {client.activity.map((ev, i) => (
                  <li key={i} className="relative pl-4 text-xs" data-testid={`sales-activity-${i}`}>
                    <span className="absolute left-0 top-1 h-1.5 w-1.5 rounded-full bg-slate-300" />
                    <div className="text-slate-700">
                      <span className="font-medium">{ev.by_name || ev.by}</span>{" "}
                      {ev.type === "status" && <>changed status <span className="text-slate-500">{ev.from}</span> → <span className="font-medium text-slate-900">{ev.to}</span></>}
                      {ev.type === "assign" && <>assigned to <span className="font-medium text-slate-900">{ev.to}</span></>}
                      {ev.type === "note" && <>added a note</>}
                    </div>
                    {ev.type === "note" && ev.text && (
                      <p className="mt-0.5 text-slate-500 italic whitespace-pre-wrap">"{ev.text}"</p>
                    )}
                    <div className="text-[0.65rem] text-slate-400 mt-0.5">{formatDate(ev.at)}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
